import {
  Autocomplete,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { forwardRef, useMemo } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { CreateMealAction } from '../../../../../store/create-meal-slice';
import { queryKeys } from '../../../../../utils/queryKeys';
import {
  areMealIngredientsEqual,
  dedupeMealIngredients,
  formatMealIngredientLabel,
  mapApiIngredientToMealIngredient,
  sortMealIngredients,
} from '../../../../../utils/mealIngredientUtils';
import { getAllIngredients } from '../../../ingredient_management/IngredientManagementApi';
import AddNewIngredientDialog from './AddNewIngredientDialog';
import IngredientItemComponent from './IngredientItemComponent';

const NoItemAvailable = () => (
  <Stack display='flex' flexDirection='column' justifyContent='center' p={4}>
    <Typography variant='s18_w500'>No Item Added</Typography>
  </Stack>
);

const FoodBoxTempIngredientsSection = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { primaryIngredient } = useSelector((state) => state.createMeal);

  const { data, isLoading, isError } = useQuery(
    queryKeys.getAllIngredientsData,
    getAllIngredients,
    {
      refetchOnWindowFocus: false,
    }
  );

  const availableIngredients = useMemo(
    () =>
      sortMealIngredients(
        (data?.data ?? []).map((ingredient) =>
          mapApiIngredientToMealIngredient(ingredient)
        )
      ),
    [data?.data]
  );

  const selectableIngredients = useMemo(
    () =>
      sortMealIngredients(
        dedupeMealIngredients([...availableIngredients, ...primaryIngredient])
      ),
    [availableIngredients, primaryIngredient]
  );

  const handleIngredientSelected = (_, ingredient) => {
    if (!ingredient) {
      return;
    }

    dispatch(
      CreateMealAction.addNewIngredient({
        type: 'primary',
        data: ingredient,
      })
    );
  };

  const handleIngredientCreated = (ingredient) => {
    dispatch(
      CreateMealAction.addNewIngredient({
        type: 'primary',
        data: ingredient,
      })
    );
  };

  return (
    <Stack
      ref={ref}
      sx={{
        rowGap: 3,
        p: 3,
        border: '1px solid #BDBDBD',
        borderRadius: '5px',
      }}
    >
      <Stack direction='row' justifyContent='space-between'>
        <Typography variant='s20_w600'>Food Box Ingredients</Typography>
        <Stack direction='row' alignItems='center' columnGap={1}>
          <ErrorOutlineIcon
            style={{ color: '#F5825E' }}
            sx={{
              ':hover': {
                cursor: 'pointer',
              },
            }}
          />
          <Typography
            variant='s14_w500'
            sx={{
              color: '#F5825E',
              textDecoration: 'underline',
              ':hover': {
                cursor: 'pointer',
              },
            }}
          >
            Learn more about food items
          </Typography>
        </Stack>
      </Stack>
      <hr />
      <Grid2 container spacing={2}>
        <Grid2
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: 2,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Typography variant='s16_w500' component='span'>
              Primary Ingredient*
            </Typography>
            <AddNewIngredientDialog
              variant='contained'
              availableIngredients={data?.data ?? []}
              onIngredientCreated={handleIngredientCreated}
            />
          </Stack>

          <Autocomplete
            options={selectableIngredients}
            value={null}
            loading={isLoading}
            onChange={handleIngredientSelected}
            isOptionEqualToValue={(option, value) =>
              areMealIngredientsEqual(option, value)
            }
            getOptionLabel={(option) => formatMealIngredientLabel(option)}
            noOptionsText='No ingredients found'
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Search and select existing ingredients'
                helperText={
                  isError
                    ? 'Unable to load existing ingredients right now. You can still create one below.'
                    : 'Assign existing ingredients to this meal, or create a new one.'
                }
                error={isError}
              />
            )}
          />

          {primaryIngredient.length > 0 ? (
            primaryIngredient.map((item) => (
              <IngredientItemComponent
                key={`${item.id ?? 'ingredient'}-${item.english}-${item.nor}`}
                item={item}
                type='primary'
              />
            ))
          ) : (
            <NoItemAvailable />
          )}
        </Grid2>
      </Grid2>
    </Stack>
  );
});

export default FoodBoxTempIngredientsSection;
